import { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { api } from '../lib/api'
import { isApproved } from '../lib/auth'
import type { ChatThread, ChatMessage } from '../lib/types'

const STORAGE_KEY = 'automate-ai-chats'
const AVA_NAME = 'Ava'
const PROACTIVE_DELAY = 12000
const PROACTIVE_STORAGE = 'ava-proactive-shown'

function FullBodyAvatar({ speaking = false, thinking = false, size = 280 }: { speaking?: boolean; thinking?: boolean; size?: number }) {
  return (
    <div className={`ava-hologram ${speaking ? 'ava-speaking' : ''} ${thinking ? 'ava-thinking' : ''}`} style={{ width: size, height: size }}>
      <svg viewBox="0 0 280 400" width={size} height={(size / 280) * 400} className="ava-body-svg">
        <defs>
          <linearGradient id="ava-skin" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fdd8b5" />
            <stop offset="100%" stopColor="#f5c69e" />
          </linearGradient>
          <linearGradient id="ava-hair-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1e1b4b" />
            <stop offset="100%" stopColor="#312e81" />
          </linearGradient>
          <linearGradient id="ava-suit" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="50%" stopColor="#4f46e5" />
            <stop offset="100%" stopColor="#3730a3" />
          </linearGradient>
          <linearGradient id="ava-suit-glow" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#818cf8" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="holo-ring" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.8" />
            <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.8" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="soft-glow">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Holographic base platform */}
        <ellipse cx="140" cy="390" rx="80" ry="8" fill="url(#holo-ring)" opacity="0.5" className="ava-platform" />
        <ellipse cx="140" cy="390" rx="60" ry="5" fill="#06b6d4" opacity="0.2" />

        {/* Legs */}
        <path d="M115 310 L108 375 L100 378 L120 378 L118 320" fill="url(#ava-suit)" />
        <path d="M165 310 L172 375 L180 378 L160 378 L162 320" fill="url(#ava-suit)" />
        {/* Shoes */}
        <path d="M97 375 Q95 382 100 385 L124 385 Q126 382 124 378 L100 378" fill="#1e1b4b" />
        <path d="M176 375 Q183 382 180 385 L156 385 Q154 382 156 378 L180 378" fill="#1e1b4b" />

        {/* Body / Torso — Futuristic suit */}
        <path d="M100 200 Q90 220 88 260 Q86 300 108 320 L172 320 Q194 300 192 260 Q190 220 180 200 Z" fill="url(#ava-suit)" />
        {/* Suit detail lines */}
        <path d="M140 200 L140 320" stroke="#818cf8" strokeWidth="1" opacity="0.4" />
        <path d="M110 240 L170 240" stroke="#818cf8" strokeWidth="0.5" opacity="0.3" />
        <path d="M108 270 L172 270" stroke="#818cf8" strokeWidth="0.5" opacity="0.3" />
        {/* Chest emblem — glowing circuit */}
        <circle cx="140" cy="230" r="12" fill="none" stroke="#06b6d4" strokeWidth="1.5" opacity="0.8" filter="url(#glow)" className="ava-chest-glow" />
        <circle cx="140" cy="230" r="6" fill="#06b6d4" opacity="0.6" className="ava-chest-glow" />
        <path d="M128 230 L134 230 M146 230 L152 230 M140 218 L140 224 M140 236 L140 242" stroke="#06b6d4" strokeWidth="1" opacity="0.5" />

        {/* Arms */}
        <path d="M100 200 Q80 210 72 240 Q68 260 75 280 Q78 285 82 282 Q86 268 88 250 Q90 235 98 218" fill="url(#ava-skin)" />
        <path d="M100 200 Q84 210 78 235 Q74 250 78 260" fill="url(#ava-suit)" />
        <path d="M180 200 Q200 210 208 240 Q212 260 205 280 Q202 285 198 282 Q194 268 192 250 Q190 235 182 218" fill="url(#ava-skin)" />
        <path d="M180 200 Q196 210 202 235 Q206 250 202 260" fill="url(#ava-suit)" />

        {/* Hands */}
        <ellipse cx="77" cy="282" rx="8" ry="10" fill="url(#ava-skin)" />
        <ellipse cx="203" cy="282" rx="8" ry="10" fill="url(#ava-skin)" />

        {/* Neck */}
        <path d="M125 175 Q126 190 128 200 L152 200 Q154 190 155 175" fill="url(#ava-skin)" />

        {/* Collar — high-tech */}
        <path d="M118 198 Q130 205 140 207 Q150 205 162 198 Q165 194 162 192 Q150 198 140 200 Q130 198 118 192 Q115 194 118 198" fill="#4338ca" />

        {/* Head */}
        <ellipse cx="140" cy="145" rx="38" ry="42" fill="url(#ava-skin)" />

        {/* Hair */}
        <path d="M102 130 Q100 95 115 80 Q130 68 140 66 Q150 68 165 80 Q180 95 178 130 Q175 115 160 108 Q140 100 120 108 Q105 115 102 130" fill="url(#ava-hair-grad)" />
        {/* Side hair */}
        <path d="M102 130 Q98 145 100 165 Q102 160 104 150 Q106 140 104 132" fill="url(#ava-hair-grad)" />
        <path d="M178 130 Q182 145 180 165 Q178 160 176 150 Q174 140 176 132" fill="url(#ava-hair-grad)" />
        {/* Bangs */}
        <path d="M108 115 Q115 95 130 90 Q120 100 115 115" fill="url(#ava-hair-grad)" />
        <path d="M172 115 Q165 95 150 90 Q160 100 165 115" fill="url(#ava-hair-grad)" />

        {/* Eyes */}
        <ellipse cx="125" cy="140" rx="6" ry="6.5" fill="white" />
        <ellipse cx="155" cy="140" rx="6" ry="6.5" fill="white" />
        <ellipse cx="125" cy="141" rx="4" ry="4.5" fill="#4338ca" className="ava-eye" />
        <ellipse cx="155" cy="141" rx="4" ry="4.5" fill="#4338ca" className="ava-eye" />
        <circle cx="126.5" cy="139" r="1.8" fill="white" />
        <circle cx="156.5" cy="139" r="1.8" fill="white" />
        {/* Eye glow (when thinking/speaking) */}
        <ellipse cx="125" cy="141" rx="5" ry="5.5" fill="none" stroke="#06b6d4" strokeWidth="1" opacity="0" className="ava-eye-glow" />
        <ellipse cx="155" cy="141" rx="5" ry="5.5" fill="none" stroke="#06b6d4" strokeWidth="1" opacity="0" className="ava-eye-glow" />

        {/* Eyebrows */}
        <path d="M115 128 Q125 123 135 128" stroke="#312e81" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <path d="M145 128 Q155 123 165 128" stroke="#312e81" strokeWidth="2.5" fill="none" strokeLinecap="round" />

        {/* Nose */}
        <path d="M137 150 Q140 156 143 150" stroke="#e8c49e" strokeWidth="1.5" fill="none" strokeLinecap="round" />

        {/* Mouth — changes with speaking */}
        <path d="M127 162 Q140 172 153 162" stroke="#c0392b" strokeWidth="2" fill="none" strokeLinecap="round" className="ava-mouth" />

        {/* Blush */}
        <circle cx="113" cy="152" r="6" fill="#f8a4a4" opacity="0.25" />
        <circle cx="167" cy="152" r="6" fill="#f8a4a4" opacity="0.25" />

        {/* Tech earpiece */}
        <rect x="98" y="135" width="5" height="14" rx="2" fill="#4338ca" />
        <circle cx="100" cy="142" r="2" fill="#06b6d4" className="ava-chest-glow" />

        {/* Floating holographic data particles */}
        <circle cx="70" cy="200" r="2" fill="#06b6d4" opacity="0.6" className="ava-particle p1" />
        <circle cx="210" cy="220" r="1.5" fill="#8b5cf6" opacity="0.5" className="ava-particle p2" />
        <circle cx="85" cy="160" r="1.5" fill="#06b6d4" opacity="0.4" className="ava-particle p3" />
        <circle cx="195" cy="170" r="2" fill="#8b5cf6" opacity="0.6" className="ava-particle p4" />
        <rect x="60" y="250" width="8" height="1" rx="0.5" fill="#06b6d4" opacity="0.3" className="ava-particle p1" />
        <rect x="205" y="260" width="6" height="1" rx="0.5" fill="#8b5cf6" opacity="0.3" className="ava-particle p3" />
      </svg>
    </div>
  )
}

function MiniAvatar({ size = 36, speaking = false }: { size?: number; speaking?: boolean }) {
  return (
    <div className={`relative inline-flex items-center justify-center rounded-full ${speaking ? 'ava-speaking' : ''}`} style={{ width: size, height: size }}>
      <svg viewBox="0 0 120 120" width={size} height={size} className="rounded-full">
        <defs>
          <linearGradient id="mini-bg" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
          <linearGradient id="mini-hair" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1e1b4b" />
            <stop offset="100%" stopColor="#312e81" />
          </linearGradient>
        </defs>
        <circle cx="60" cy="60" r="60" fill="url(#mini-bg)" />
        <ellipse cx="60" cy="42" rx="32" ry="30" fill="url(#mini-hair)" />
        <ellipse cx="60" cy="55" rx="34" ry="20" fill="url(#mini-hair)" />
        <ellipse cx="60" cy="55" rx="26" ry="28" fill="#fdd8b5" />
        <ellipse cx="48" cy="52" rx="4" ry="4.5" fill="#4338ca" />
        <ellipse cx="72" cy="52" rx="4" ry="4.5" fill="#4338ca" />
        <circle cx="49.5" cy="50.5" r="1.5" fill="white" />
        <circle cx="73.5" cy="50.5" r="1.5" fill="white" />
        <path d="M42 44 Q48 40 54 44" stroke="#312e81" strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d="M66 44 Q72 40 78 44" stroke="#312e81" strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d="M58 58 Q60 62 62 58" stroke="#e8c49e" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <path d="M50 66 Q60 74 70 66" stroke="#c0392b" strokeWidth="2" fill="none" strokeLinecap="round" />
        <circle cx="42" cy="62" r="5" fill="#f8a4a4" opacity="0.3" />
        <circle cx="78" cy="62" r="5" fill="#f8a4a4" opacity="0.3" />
        <path d="M30 42 Q35 25 50 28 Q42 35 40 45" fill="url(#mini-hair)" />
        <path d="M90 42 Q85 25 70 28 Q78 35 80 45" fill="url(#mini-hair)" />
        <path d="M50 28 Q60 22 70 28 Q65 30 60 30 Q55 30 50 28" fill="url(#mini-hair)" />
        <path d="M30 95 Q35 80 60 78 Q85 80 90 95 L90 120 L30 120 Z" fill="#4f46e5" />
        <circle cx="60" cy="88" r="3" fill="#06b6d4" opacity="0.7" />
        <path d="M50 80 L60 90 L70 80" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" />
      </svg>
      <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-green-500 border-2 border-white" />
      </span>
    </div>
  )
}

type PageContext = {
  page: string
  companyId?: string
  category?: string
  search?: string
}

function getPageContext(pathname: string, searchStr: string): PageContext {
  const params = new URLSearchParams(searchStr)
  if (pathname === '/') return { page: 'home' }
  if (pathname === '/tracks') return { page: 'tracks' }
  if (pathname === '/profile') return { page: 'profile' }
  if (pathname === '/admin') return { page: 'admin' }
  if (pathname.startsWith('/companies/')) return { page: 'company-detail', companyId: pathname.split('/')[2] }
  if (pathname === '/companies') return { page: 'companies', category: params.get('category') || undefined, search: params.get('search') || undefined }
  return { page: 'other' }
}

const PROACTIVE_TIPS: Record<string, string[]> = {
  home: [
    "Welcome to Automate 2026! The show floor is massive — 450K sq ft across South & North halls. Want me to build you a personalized route based on your interests?",
    "Pro tip: The NVIDIA Humanoid Pavilion (Booth 2284) tends to get packed after 11am. I'd recommend going first thing if humanoid robots are on your list.",
    "There are 5 curated navigation tracks with 36 stops total. Check the Tracks page — I've priority-ranked each stop so you hit the must-sees first.",
  ],
  tracks: [
    "Each track is designed for 2-3 hours of walking. The Humanoid & Physical AI track has the biggest crowds — start there if it's a priority.",
    "Tip: Booth numbers starting with 1xxxx or 2xxxx are in North Hall — it's a 5-minute walk from the South Hall main entrance. Plan your route accordingly!",
  ],
  companies: [
    "Browsing exhibitors? Try filtering by category — there are 12 categories from Robotics to Humanoid Robots. The AI Intel tab on each company has deep research reports.",
    "Quick find: Use booth numbers if someone told you to visit a specific booth. Type the number in the search bar to jump right to it.",
  ],
  'company-detail': [
    "Don't forget to check the AI Intel tab — it has a full intelligence report with company financials, social media presence, and recent news.",
    "Record a voice memo while at this booth! The recording feature auto-saves to your account and can generate AI summaries later.",
    "Pro tip: Use the Smart Questions across all 5 categories — they're designed to help you evaluate vendors quickly and consistently.",
  ],
  profile: [
    "Your notes and recordings are synced to your account. You can access them from any device by signing in with the same email.",
  ],
}

function loadThreads(): ChatThread[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
  } catch { return [] }
}

function saveThreads(threads: ChatThread[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(threads))
}

function newThread(): ChatThread {
  return {
    id: crypto.randomUUID(),
    title: 'New Chat',
    messages: [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function getShownProactive(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(PROACTIVE_STORAGE) || '[]'))
  } catch { return new Set() }
}

function markProactiveShown(key: string) {
  const shown = getShownProactive()
  shown.add(key)
  localStorage.setItem(PROACTIVE_STORAGE, JSON.stringify([...shown]))
}

export default function AICompanion() {
  const [open, setOpen] = useState(false)
  const [threads, setThreads] = useState<ChatThread[]>(loadThreads)
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
  const [showThreadList, setShowThreadList] = useState(false)
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [listening, setListening] = useState(false)
  const [speaking, setSpeaking] = useState(false)
  const [voiceEnabled, setVoiceEnabled] = useState(true)
  const [showMap, setShowMap] = useState(false)
  const [proactiveTip, setProactiveTip] = useState<string | null>(null)
  const [showFullAvatar, setShowFullAvatar] = useState(false)
  const chatEndRef = useRef<HTMLDivElement>(null)
  const recognitionRef = useRef<any>(null)
  const synthRef = useRef(window.speechSynthesis)
  const inputRef = useRef<HTMLInputElement>(null)
  const proactiveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const location = useLocation()

  const approved = isApproved()

  const activeThread = threads.find(t => t.id === activeThreadId) || null
  const messages = activeThread?.messages || []

  const pageContext = getPageContext(location.pathname, location.search)

  useEffect(() => {
    if (threads.length === 0) {
      const t = newThread()
      setThreads([t])
      setActiveThreadId(t.id)
    } else if (!activeThreadId) {
      setActiveThreadId(threads[0].id)
    }
  }, [])

  useEffect(() => { saveThreads(threads) }, [threads])
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])
  useEffect(() => { if (open) inputRef.current?.focus() }, [open])

  useEffect(() => {
    if (!approved || open) return
    if (proactiveTimerRef.current) clearTimeout(proactiveTimerRef.current)

    proactiveTimerRef.current = setTimeout(() => {
      const tips = PROACTIVE_TIPS[pageContext.page] || []
      const shown = getShownProactive()
      const key = `${pageContext.page}-${pageContext.companyId || ''}-${pageContext.category || ''}`
      const available = tips.filter((_, i) => !shown.has(`${pageContext.page}-${i}`))
      if (available.length > 0) {
        const tipIndex = tips.indexOf(available[0])
        setProactiveTip(available[0])
        markProactiveShown(`${pageContext.page}-${tipIndex}`)
      }
    }, PROACTIVE_DELAY)

    return () => { if (proactiveTimerRef.current) clearTimeout(proactiveTimerRef.current) }
  }, [location.pathname, location.search, approved, open])

  const speak = useCallback((text: string) => {
    if (!voiceEnabled) return
    synthRef.current.cancel()
    const clean = text.replace(/[#*_`]/g, '').replace(/\[.*?\]/g, '')
    const utterance = new SpeechSynthesisUtterance(clean)
    utterance.rate = 1.05
    utterance.pitch = 1.1
    utterance.onstart = () => setSpeaking(true)
    utterance.onend = () => setSpeaking(false)
    utterance.onerror = () => setSpeaking(false)
    synthRef.current.speak(utterance)
  }, [voiceEnabled])

  const stopSpeaking = () => { synthRef.current.cancel(); setSpeaking(false) }

  function updateThread(threadId: string, updater: (t: ChatThread) => ChatThread) {
    setThreads(prev => prev.map(t => t.id === threadId ? updater(t) : t))
  }

  async function sendMessage(text: string) {
    if (!text.trim() || loading) return
    const tid = activeThreadId
    if (!tid) return

    setProactiveTip(null)
    const userMsg: ChatMessage = { role: 'user', content: text.trim(), timestamp: new Date().toISOString() }

    updateThread(tid, t => ({
      ...t,
      messages: [...t.messages, userMsg],
      title: t.messages.length === 0 ? text.trim().slice(0, 40) : t.title,
      updated_at: new Date().toISOString(),
    }))
    setInput('')
    setLoading(true)

    try {
      const contextInfo = `[User is on: ${pageContext.page}${pageContext.companyId ? ` — viewing company ${pageContext.companyId}` : ''}${pageContext.category ? ` — filtered to ${pageContext.category}` : ''}]`
      const currentMessages = [...messages, userMsg]
      const { reply } = await api.aiChat(
        currentMessages.map(m => ({ role: m.role, content: m.content })),
        contextInfo
      )
      const aiMsg: ChatMessage = { role: 'assistant', content: reply, timestamp: new Date().toISOString() }
      updateThread(tid, t => ({ ...t, messages: [...t.messages, aiMsg], updated_at: new Date().toISOString() }))
      speak(reply)
    } catch {
      const errMsg: ChatMessage = { role: 'assistant', content: "I'm having trouble connecting right now. Please try again in a moment.", timestamp: new Date().toISOString() }
      updateThread(tid, t => ({ ...t, messages: [...t.messages, errMsg] }))
    } finally {
      setLoading(false)
    }
  }

  function startListening() {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SR) { alert('Voice input not supported. Try Chrome or Edge.'); return }
    if (listening) { recognitionRef.current?.stop(); setListening(false); return }
    const recognition = new SR()
    recognition.continuous = false
    recognition.interimResults = false
    recognition.lang = 'en-US'
    recognition.onresult = (e: any) => { setListening(false); sendMessage(e.results[0][0].transcript) }
    recognition.onerror = () => setListening(false)
    recognition.onend = () => setListening(false)
    recognitionRef.current = recognition
    recognition.start()
    setListening(true)
  }

  function createNewChat() {
    const t = newThread()
    setThreads(prev => [t, ...prev])
    setActiveThreadId(t.id)
    setShowThreadList(false)
    stopSpeaking()
  }

  function deleteThread(id: string) {
    setThreads(prev => {
      const filtered = prev.filter(t => t.id !== id)
      if (activeThreadId === id) {
        if (filtered.length > 0) setActiveThreadId(filtered[0].id)
        else { const t = newThread(); setActiveThreadId(t.id); return [t] }
      }
      return filtered
    })
  }

  function switchThread(id: string) {
    setActiveThreadId(id)
    setShowThreadList(false)
    stopSpeaking()
  }

  function handleProactiveTipClick(tip: string) {
    setProactiveTip(null)
    setOpen(true)
    setTimeout(() => sendMessage(tip), 300)
  }

  if (!approved) return null

  const quickSuggestions = pageContext.page === 'company-detail'
    ? [
        { q: 'Give me a quick briefing on this company', icon: '📊' },
        { q: 'What smart questions should I ask at this booth?', icon: '❓' },
        { q: 'Who are their main competitors here?', icon: '⚔️' },
        { q: 'What should I look for in their demo?', icon: '👁️' },
      ]
    : pageContext.page === 'companies'
    ? [
        { q: 'Which exhibitors should I prioritize?', icon: '⭐' },
        { q: 'Show me the must-see robotics companies', icon: '🤖' },
        { q: 'What booths are near each other?', icon: '🗺️' },
        { q: 'Any hidden gem companies I might miss?', icon: '💎' },
      ]
    : pageContext.page === 'tracks'
    ? [
        { q: 'Which track should I start with?', icon: '🏁' },
        { q: 'How long will the Humanoid track take?', icon: '⏱️' },
        { q: 'Can I combine two tracks in one day?', icon: '🔀' },
        { q: 'What are the top demos on each track?', icon: '🎯' },
      ]
    : [
        { q: 'Where is the NVIDIA pavilion?', icon: '📍' },
        { q: 'Best route from FANUC to Cognex?', icon: '🗺️' },
        { q: 'Where can I eat lunch nearby?', icon: '🍽️' },
        { q: 'Which humanoid robots are here?', icon: '🤖' },
        { q: 'What keynotes are today?', icon: '🎤' },
      ]

  return (
    <>
      {/* Proactive tip bubble */}
      {proactiveTip && !open && (
        <div className="fixed bottom-24 right-6 z-50 max-w-[320px] ava-proactive-enter">
          <div className="bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-600 text-white rounded-2xl rounded-br-md p-4 shadow-2xl shadow-indigo-500/20 cursor-pointer hover:scale-[1.02] transition-transform"
            onClick={() => handleProactiveTipClick(proactiveTip)}
          >
            <div className="flex items-start gap-2">
              <span className="text-lg shrink-0">💡</span>
              <div>
                <p className="text-sm leading-relaxed">{proactiveTip}</p>
                <p className="text-xs text-indigo-200 mt-2">Tap to chat about this</p>
              </div>
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setProactiveTip(null) }}
            className="absolute -top-2 -right-2 w-6 h-6 bg-gray-800 text-white rounded-full text-xs flex items-center justify-center hover:bg-gray-700 shadow-lg"
          >
            ✕
          </button>
        </div>
      )}

      {/* FAB button with avatar */}
      <button
        onClick={() => { setOpen(!open); setProactiveTip(null) }}
        className={`fixed bottom-6 right-6 z-50 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 ${
          open ? 'w-14 h-14 bg-slate-800 rotate-0 hover:bg-slate-700'
            : 'w-16 h-16 ai-fab-pulse hover:scale-110'
        }`}
      >
        {open ? (
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <MiniAvatar size={56} />
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-24 right-4 z-50 w-[420px] max-w-[calc(100vw-2rem)] h-[620px] max-h-[calc(100vh-8rem)] glass rounded-3xl shadow-2xl flex flex-col overflow-hidden chat-panel-enter"
          style={{ boxShadow: '0 25px 60px -12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 80px -20px rgba(99,102,241,0.15)' }}
        >
          {/* Header with avatar */}
          <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 text-white px-5 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => setShowFullAvatar(!showFullAvatar)}>
              <MiniAvatar size={40} speaking={speaking} />
              <div>
                <div className="font-bold text-[15px] flex items-center gap-2">
                  {AVA_NAME}
                  {speaking && <span className="text-xs font-normal text-indigo-200 animate-pulse">speaking...</span>}
                  {loading && <span className="text-xs font-normal text-cyan-200 animate-pulse">thinking...</span>}
                </div>
                <div className="text-xs text-indigo-200 flex items-center gap-1.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400" />
                  Your AI Conference Guide
                </div>
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              <button onClick={() => setShowThreadList(!showThreadList)} className="p-2 rounded-xl hover:bg-white/15 transition-colors" title="Chat history">
                <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </button>
              <button onClick={() => setShowMap(!showMap)} className={`p-2 rounded-xl transition-colors ${showMap ? 'bg-white/25' : 'hover:bg-white/15'}`} title="Floor map">
                <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </button>
              <button onClick={() => setVoiceEnabled(!voiceEnabled)} className={`p-2 rounded-xl transition-colors ${voiceEnabled ? 'hover:bg-white/15' : 'bg-white/5 opacity-50 hover:opacity-75'}`} title={voiceEnabled ? 'Voice ON' : 'Voice OFF'}>
                <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {voiceEnabled
                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  }
                </svg>
              </button>
              <button onClick={createNewChat} className="p-2 rounded-xl hover:bg-white/15 transition-colors" title="New chat">
                <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>

          {/* Full avatar overlay */}
          {showFullAvatar && !showThreadList && !showMap && (
            <div className="absolute top-[64px] left-0 right-0 bottom-[64px] z-10 overflow-hidden flex items-center justify-center ava-avatar-overlay"
              onClick={() => setShowFullAvatar(false)}
            >
              <div className="relative" onClick={e => e.stopPropagation()}>
                <FullBodyAvatar speaking={speaking} thinking={loading} size={240} />
                <p className="text-center text-xs text-indigo-300 mt-2 font-medium">
                  {speaking ? '🔊 Speaking...' : loading ? '🧠 Thinking...' : '✨ Ready to help'}
                </p>
              </div>
            </div>
          )}

          {/* Thread list overlay */}
          {showThreadList && (
            <div className="absolute top-[64px] left-0 right-0 bottom-0 bg-white/95 backdrop-blur-sm z-10 overflow-y-auto">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold text-sm text-gray-700">Chat History</h3>
                <button onClick={createNewChat} className="text-xs bg-indigo-600 text-white px-4 py-1.5 rounded-full hover:bg-indigo-700 transition-colors font-medium">+ New Chat</button>
              </div>
              <div className="divide-y divide-gray-100">
                {threads.map(t => (
                  <div key={t.id} className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-indigo-50/50 transition-colors ${t.id === activeThreadId ? 'bg-indigo-50 border-l-3 border-indigo-600' : ''}`} onClick={() => switchThread(t.id)}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate text-gray-800">{t.title}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{t.messages.length} message{t.messages.length !== 1 ? 's' : ''} &middot; {new Date(t.updated_at).toLocaleDateString()}</div>
                    </div>
                    <button onClick={e => { e.stopPropagation(); deleteThread(t.id) }} className="text-gray-300 hover:text-red-500 p-1.5 ml-2 shrink-0 rounded-lg hover:bg-red-50 transition-colors" title="Delete">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Map overlay */}
          {showMap && !showThreadList && (
            <div className="absolute top-[64px] left-0 right-0 bottom-[64px] bg-white/95 backdrop-blur-sm z-10 overflow-y-auto p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-sm text-gray-700">McCormick Place</h3>
                <button onClick={() => setShowMap(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="space-y-2.5 text-xs stagger-children">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-3.5 border border-blue-100">
                  <div className="font-semibold text-blue-800 mb-1.5">📍 Layout</div>
                  <div className="text-blue-700 space-y-0.5">
                    <p><strong>South:</strong> Booths 100-4000 (Main Expo)</p>
                    <p><strong>North B:</strong> 12000+, 28000+</p>
                    <p><strong>North C:</strong> 32000+, 34000+</p>
                    <p><strong>Hours:</strong> Mon-Wed 10-5, Thu 10-3</p>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-3.5 border border-green-100">
                  <div className="font-semibold text-green-800 mb-1.5">🛒 Food</div>
                  <div className="text-green-700 space-y-0.5">
                    <p><strong>Grand Concourse:</strong> Level 2.5</p>
                    <p>American Grill, Little Italy, Pacific Rim, Express</p>
                    <p><strong>23rd St Cafe:</strong> Level 3 (healthy)</p>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-3.5 border border-purple-100">
                  <div className="font-semibold text-purple-800 mb-1.5">⭐ Key Booths</div>
                  <div className="grid grid-cols-2 gap-0.5 text-purple-700">
                    {[['NVIDIA','2284'],['Boston Dynamics','225'],['FANUC','1001'],['Universal Robots','1250'],['ABB','1241'],['Cognex','3101'],['Siemens','1214'],['Agility','1891'],['NEURA','2231'],['Kawasaki','2201'],['Keyence','667'],['Schneider','2257']].map(([n,b]) => (
                      <div key={n} className="flex justify-between"><span className="truncate">{n}</span><span className="font-mono text-indigo-600 ml-1 font-medium">{b}</span></div>
                    ))}
                  </div>
                </div>
                <a href="https://automate26.mapyourshow.com/8_0/floorplan/index.cfm" target="_blank" rel="noopener noreferrer" className="block bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl p-3 text-center font-medium hover:from-indigo-500 hover:to-purple-500 transition-all">
                  Open Interactive Floor Plan &rarr;
                </a>
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
            {messages.length === 0 && (
              <div className="text-center mt-2 space-y-3">
                <div className="cursor-pointer" onClick={() => setShowFullAvatar(true)}>
                  <FullBodyAvatar speaking={speaking} thinking={loading} size={180} />
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-lg">Hi, I'm {AVA_NAME}!</p>
                  <p className="text-sm text-gray-500 mt-1">Your AI guide for Automate 2026</p>
                  <p className="text-xs text-indigo-400 mt-0.5">I can see what page you're on and offer relevant help</p>
                </div>
                <div className="space-y-1.5 stagger-children">
                  {quickSuggestions.map(({ q, icon }) => (
                    <button key={q} onClick={() => sendMessage(q)} className="flex items-center gap-2.5 w-full text-left text-sm bg-gray-50 hover:bg-indigo-50 rounded-xl px-4 py-2.5 transition-all hover:shadow-sm border border-transparent hover:border-indigo-100 group">
                      <span className="text-base">{icon}</span>
                      <span className="text-gray-700 group-hover:text-indigo-700">{q}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2.5 chat-msg-enter ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                {msg.role === 'assistant' && <MiniAvatar size={32} speaking={speaking && i === messages.length - 1} />}
                <div className="flex flex-col gap-0.5 max-w-[80%]">
                  <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-indigo-600 to-violet-600 text-white rounded-br-md shadow-sm'
                      : 'bg-gray-100 text-gray-800 rounded-bl-md'
                  }`}>
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  </div>
                  <span className={`text-[10px] text-gray-400 ${msg.role === 'user' ? 'text-right' : 'text-left ml-1'}`}>
                    {msg.role === 'assistant' ? AVA_NAME + ' · ' : ''}{formatTime(msg.timestamp)}
                  </span>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-2.5 chat-msg-enter">
                <MiniAvatar size={32} speaking />
                <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3.5">
                  <div className="flex gap-1.5 items-center">
                    <span className="w-2 h-2 bg-indigo-400 rounded-full typing-dot" />
                    <span className="w-2 h-2 bg-indigo-400 rounded-full typing-dot" />
                    <span className="w-2 h-2 bg-indigo-400 rounded-full typing-dot" />
                  </div>
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Page context indicator */}
          <div className="px-4 py-1 bg-indigo-50/80 border-t border-indigo-100 flex items-center gap-2 text-xs text-indigo-600 shrink-0">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
            <span>Aware: <strong>{pageContext.page === 'company-detail' ? `Company page` : pageContext.page === 'companies' ? (pageContext.category ? `Exhibitors (${pageContext.category})` : 'Exhibitors') : pageContext.page === 'tracks' ? 'Navigation tracks' : pageContext.page}</strong></span>
          </div>

          {/* Input area */}
          <div className="border-t border-gray-200/80 px-4 py-3 flex items-center gap-2 shrink-0 bg-white/80 backdrop-blur-sm">
            <button onClick={startListening} className={`p-2.5 rounded-xl transition-all shrink-0 ${listening ? 'bg-red-500 text-white shadow-lg shadow-red-500/25 animate-pulse' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'}`} title={listening ? 'Stop listening' : 'Voice input'}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </button>

            {speaking && (
              <button onClick={stopSpeaking} className="p-2.5 rounded-xl bg-indigo-100 text-indigo-600 animate-pulse shrink-0" title="Stop speaking">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                </svg>
              </button>
            )}

            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage(input)}
              placeholder={listening ? 'Listening...' : `Ask ${AVA_NAME} anything...`}
              className="flex-1 bg-gray-100 border-0 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
              disabled={loading || listening}
              spellCheck={true}
              autoComplete="off"
              autoCorrect="on"
            />

            <button onClick={() => sendMessage(input)} disabled={loading || !input.trim()} className="p-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:from-indigo-500 hover:to-violet-500 disabled:opacity-30 disabled:hover:from-indigo-600 disabled:hover:to-violet-600 transition-all shrink-0 shadow-sm">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  )
}
